// src/pages/Tuition.jsx
import React, { useState } from "react";
import { SEO } from "../components/common/SEO";
import { motion } from "framer-motion";
import {
  HiAcademicCap,
  HiHome,
  HiCheckCircle,
  HiClock,
  HiUserGroup,
  HiPhone,
  HiLightningBolt,
  HiStar,
} from "react-icons/hi";

/* ── Animation helpers ──────────────────────────────────────────── */
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

/* ── Floating math symbol ────────────────────────────────────────── */
function MathSymbol({ symbol, className, delay = 0, duration = 6 }) {
  return (
    <motion.span
      aria-hidden
      className={`absolute select-none pointer-events-none font-bold text-white/20 ${className}`}
      animate={{ y: [0, -16, 0], rotate: [0, 8, 0] }}
      transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
    >
      {symbol}
    </motion.span>
  );
}

/* ── Data ────────────────────────────────────────────────────────── */
const CENTRE_POINTS = [
  "Small batches — maximum 8 students",
  "State Board & CBSE, Classes 1 to 12",
  "Concept-first teaching, not rote learning",
  "Weekly tests with detailed feedback",
  "Dedicated doubt-clearing hours",
  "Structured exam preparation strategies",
];

const HOME_POINTS = [
  "One-on-one sessions at your home",
  "Flexible timings around your schedule",
  "Personalised plan for each student",
  "Same teacher every session — consistency",
  "Focus on weak areas & exam gaps",
  "Regular progress updates for parents",
];

const TOPICS = [
  { symbol: "x²", label: "Algebra" },
  { symbol: "π",  label: "Geometry" },
  { symbol: "sin", label: "Trigonometry" },
  { symbol: "∫",  label: "Calculus" },
  { symbol: "∑",  label: "Statistics" },
  { symbol: "√",  label: "Arithmetic" },
];

const STATS = [
  { value: "5000+", label: "Students Taught",    icon: <HiUserGroup className="text-xl text-blue-600" /> },
  { value: "35+",  label: "Years Experience",   icon: <HiStar className="text-xl text-blue-600" /> },
  { value: "1–12", label: "Classes Covered",    icon: <HiAcademicCap className="text-xl text-blue-600" /> },
  { value: "98%",  label: "Pass Rate",          icon: <HiLightningBolt className="text-xl text-blue-600" /> },
];

/* ── Component ───────────────────────────────────────────────────── */
export default function Tuition() {
  const [enquiryName, setEnquiryName] = useState("");
  const [enquiryPhone, setEnquiryPhone] = useState("");
  const [tuitionType, setTuitionType] = useState(""); // "home" | "centre"
  const [showMap, setShowMap] = useState(false);
  const [error, setError] = useState("");

  const handleEnquiry = () => {
    if (!tuitionType) { setError("Please select Home Tuition or Tuition Centre."); return; }
    setError("");
    if (tuitionType === "home") {
      const msg = encodeURIComponent(
        `Hi, I am ${enquiryName || "a student"} and I am interested in *Home Tuition*.${enquiryPhone ? ` My WhatsApp number is ${enquiryPhone}.` : ""} Please get in touch.`
      );
      window.open(`https://wa.me/918056206308?text=${msg}`, "_blank");
    } else {
      setShowMap(true);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Home Tuition &amp; Maths Tuition Centre in Chennai | Expert Coaching Classes"
        description="Expert maths home tuition and tuition centre in Chennai, Tamil Nadu. Tamil Nadu State Board &amp; CBSE, Classes 1–12. Book via WhatsApp — +91 80562 06308."
        canonical="/tuition"
      />

      {/* ════════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white py-16 px-6">

        {/* Floating math symbols */}
        <MathSymbol symbol="π"   className="text-6xl top-8  left-[4%]"    delay={0}   duration={7} />
        <MathSymbol symbol="∑"   className="text-5xl top-16 right-[4%]"   delay={1.5} duration={8} />
        <MathSymbol symbol="√"   className="text-7xl bottom-8 left-[12%]" delay={0.8} duration={6} />
        <MathSymbol symbol="∫"   className="text-5xl top-10 left-[45%]"   delay={2}   duration={9} />
        <MathSymbol symbol="∞"   className="text-4xl bottom-10 right-[8%]" delay={1}  duration={7} />
        <MathSymbol symbol="x²"  className="text-4xl top-1/3 left-[30%]"  delay={0.5} duration={8} />

        {/* Dot grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="relative mx-auto max-w-screen-lg grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* ── Left: text + CTA ── */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-white/15 border border-white/25 text-white text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-yellow-300 animate-pulse" />
              Maths Specialist · Since 2000
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4"
            >
              Maths Tuition<br />
              <span className="text-yellow-300">That Actually Works.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="text-blue-100 text-base leading-relaxed mb-8"
            >
              Expert Maths coaching at our centre or at your home — building real
              understanding, not just exam scores.
            </motion.p>

            <motion.a
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              href="https://wa.me/918056206308"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-7 py-3 rounded-full text-sm hover:bg-blue-50 transition-all shadow-lg shadow-blue-900/25"
            >
              <HiPhone /> Call / WhatsApp
            </motion.a>
          </div>

          {/* ── Right: enquiry form card ── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, delay: 0.3 }}
            className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl shadow-blue-900/30"
          >
            {showMap ? (
              /* Centre chosen → show map */
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-black text-gray-900 text-base">Find Our Centre</h3>
                  <button
                    onClick={() => { setShowMap(false); setTuitionType(""); }}
                    className="text-xs text-blue-600 font-semibold hover:underline"
                  >
                    ← Back
                  </button>
                </div>
                <div className="rounded-2xl overflow-hidden border border-gray-100 mb-4" style={{ height: "220px" }}>
                  <iframe
                    title="Ravi Maths Tuition Centre"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3885.81108064038!2d80.21106557484394!3d13.111151487217246!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52655b4cabeecd%3A0xf9bb1af6542575ea!2sRAVI%20MATHS%20TUITION%20CENTER!5e0!3m2!1sen!2sus!4v1776335063248!5m2!1sen!2sus"
                    width="100%" height="100%" style={{ border: 0 }}
                    allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <p className="text-sm font-bold text-gray-800">Ravi Maths Tuition Center</p>
                <p className="text-xs text-gray-400 mb-4">Chennai, Tamil Nadu · Mon–Sat, 9 AM – 7 PM · +91 80562 06308</p>
                <div className="flex gap-3">
                  <a
                    href="https://www.google.com/maps/place/RAVI+MATHS+TUITION+CENTER/@13.111151,80.211066,17z"
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 text-center bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-colors"
                  >
                    Get Directions
                  </a>
                  <a
                    href="https://wa.me/918056206308"
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 text-center bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-colors"
                  >
                    WhatsApp Us
                  </a>
                </div>
              </div>
            ) : (
              /* Default → enquiry form */
              <div className="space-y-4">
                <div>
                  <h3 className="font-black text-gray-900 text-base mb-0.5">Quick Enquiry</h3>
                  <p className="text-xs text-gray-400">All fields optional except your preference.</p>
                </div>

                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={enquiryName}
                  onChange={(e) => setEnquiryName(e.target.value)}
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
                />

                <input
                  type="tel"
                  placeholder="WhatsApp number (optional)"
                  value={enquiryPhone}
                  onChange={(e) => setEnquiryPhone(e.target.value)}
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
                />

                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">I want</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "home",   icon: <HiHome className="text-lg" />,        label: "Home Tuition",   sub: "Teacher comes to you" },
                      { key: "centre", icon: <HiAcademicCap className="text-lg" />, label: "Tuition Centre", sub: "Visit our classroom"   },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => { setTuitionType(opt.key); setError(""); }}
                        className={`flex flex-col items-center gap-1.5 p-3.5 rounded-2xl border-2 text-center transition-all duration-200 ${
                          tuitionType === opt.key
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 bg-white text-gray-500 hover:border-blue-200"
                        }`}
                      >
                        <span className={tuitionType === opt.key ? "text-blue-600" : "text-gray-400"}>{opt.icon}</span>
                        <span className="text-xs font-bold">{opt.label}</span>
                        <span className="text-[10px] text-gray-400">{opt.sub}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {error && <p className="text-red-500 text-xs">{error}</p>}

                <button
                  onClick={handleEnquiry}
                  className="w-full py-3 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200 transition-colors flex items-center justify-center gap-2"
                >
                  {tuitionType === "home"
                    ? <><HiPhone /> Send on WhatsApp</>
                    : tuitionType === "centre"
                    ? <><HiAcademicCap /> Show Me the Centre</>
                    : "Submit Enquiry"}
                </button>
              </div>
            )}
          </motion.div>

        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          STATS
      ════════════════════════════════════════════════════ */}
      <section className="bg-blue-50 py-10 px-6">
        <motion.div
          variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="mx-auto max-w-screen-lg grid grid-cols-2 sm:grid-cols-4 gap-5"
        >
          {STATS.map((s) => (
            <motion.div
              key={s.label}
              variants={fadeUp}
              className="bg-white rounded-2xl border border-blue-100 p-5 flex flex-col items-center text-center gap-2 shadow-sm"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                {s.icon}
              </div>
              <span className="text-2xl font-black text-blue-700">{s.value}</span>
              <span className="text-xs text-gray-400 font-medium">{s.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════
          SERVICE CARDS
      ════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-screen-lg px-6 py-16">
        <motion.div
          variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.p variants={fadeUp} className="text-blue-600 text-xs font-semibold tracking-widest uppercase mb-3">
            How We Teach
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-bold text-gray-900">
            Two Ways to Learn Maths With Us
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-6"
        >
          {/* Centre card */}
          <motion.div
            variants={fadeUp}
            whileHover={{ y: -4 }}
            className="relative bg-white border-2 border-blue-100 hover:border-blue-300 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 overflow-hidden"
          >
            {/* Decorative corner blob */}
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-blue-50" />

            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center mb-6 shadow-md shadow-blue-200">
                <HiAcademicCap className="text-2xl text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Tuition Centre</h2>
              <p className="text-blue-600 text-xs font-semibold uppercase tracking-wider mb-4">Classroom · Group sessions</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Structured Maths sessions in small batches. Learn alongside peers, take
                weekly tests, and build exam confidence under expert guidance.
              </p>
              <ul className="space-y-2.5">
                {CENTRE_POINTS.map((pt) => (
                  <li key={pt} className="flex items-start gap-3">
                    <HiCheckCircle className="text-blue-500 text-base flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-600">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Home Tuition card */}
          <motion.div
            variants={fadeUp}
            whileHover={{ y: -4 }}
            className="relative bg-white border-2 border-indigo-100 hover:border-indigo-300 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 overflow-hidden"
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-indigo-50" />

            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center mb-6 shadow-md shadow-indigo-200">
                <HiHome className="text-2xl text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Home Tuition</h2>
              <p className="text-indigo-600 text-xs font-semibold uppercase tracking-wider mb-4">At your doorstep · 1-on-1</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                Prefer learning at home? Our Maths teacher comes to you — fully
                personalised sessions built around your child's exact pace and weak areas.
              </p>
              <ul className="space-y-2.5">
                {HOME_POINTS.map((pt) => (
                  <li key={pt} className="flex items-start gap-3">
                    <HiCheckCircle className="text-indigo-500 text-base flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-600">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════
          FIND US — MAP
      ════════════════════════════════════════════════════ */}
      <section className="bg-white py-16 px-6">
        <div className="mx-auto max-w-screen-lg">
          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-center mb-10"
          >
            <motion.p variants={fadeUp} className="text-blue-600 text-xs font-semibold tracking-widest uppercase mb-3">
              Location
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-bold text-gray-900">
              Find Our Tuition Centre
            </motion.h2>
          </motion.div>

          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start"
          >
            {/* Address card */}
            <motion.div variants={fadeUp} className="flex flex-col gap-5">
              <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-6">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center mb-4">
                  {/* Map pin icon */}
                  <svg className="w-5 h-5 text-white fill-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Ravi Maths Tuition Center</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Chennai, Tamil Nadu, India
                </p>
              </div>

              <div className="bg-white border-2 border-gray-100 rounded-2xl p-6 flex flex-col gap-3">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <HiClock className="text-blue-500 flex-shrink-0 text-lg" />
                  <div>
                    <p className="font-semibold text-gray-800">Class Timings</p>
                    <p className="text-gray-400">Mon – Sat · 9 AM to 7 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <HiPhone className="text-blue-500 flex-shrink-0 text-lg" />
                  <div>
                    <p className="font-semibold text-gray-800">Contact</p>
                    <p className="text-gray-400">+91 80562 06308</p>
                  </div>
                </div>
              </div>

              <a
                href="https://share.google/eeSOfs14ZEASjyjFP"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-6 py-3 rounded-xl transition-colors"
              >
                {/* Directions arrow */}
                <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.71 11.29l-9-9a1 1 0 00-1.42 0l-9 9a1 1 0 000 1.42l9 9a1 1 0 001.42 0l9-9a1 1 0 000-1.42zM14 14.5V12h-4v3H8v-4a1 1 0 011-1h5V7.5l3.5 3.5-3.5 3.5z"/>
                </svg>
                Get Directions
              </a>
            </motion.div>

            {/* Map embed — col-span-2 */}
            <motion.div
              variants={fadeUp}
              className="md:col-span-2 rounded-2xl overflow-hidden border-2 border-gray-100 shadow-sm"
              style={{ height: "360px" }}
            >
              {/*
                TO EMBED YOUR MAP:
                1. Open Google Maps and find your centre's location
                2. Click Share → Embed a map
                3. Copy the src URL from the <iframe> code
                4. Paste it as the src below (replacing the placeholder)
              */}
              <iframe
                title="Ravi TestPapers Tuition Centre"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3885.81108064038!2d80.21106557484394!3d13.111151487217246!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52655b4cabeecd%3A0xf9bb1af6542575ea!2sRAVI%20MATHS%20TUITION%20CENTER!5e0!3m2!1sen!2sus!4v1776335063248!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          TOPICS WE COVER
      ════════════════════════════════════════════════════ */}
      <section className="bg-gray-50 py-16 px-6">
        <div className="mx-auto max-w-screen-lg">
          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-center mb-10"
          >
            <motion.p variants={fadeUp} className="text-blue-600 text-xs font-semibold tracking-widest uppercase mb-3">
              Curriculum
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-bold text-gray-900">
              Maths Topics We Cover
            </motion.h2>
          </motion.div>

          <motion.div
            variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid grid-cols-3 sm:grid-cols-6 gap-4"
          >
            {TOPICS.map((t) => (
              <motion.div
                key={t.label}
                variants={fadeUp}
                whileHover={{ y: -4, scale: 1.04 }}
                className="bg-white border-2 border-gray-100 hover:border-blue-200 rounded-2xl p-4 flex flex-col items-center gap-2 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <span className="text-2xl font-black text-blue-600">{t.symbol}</span>
                <span className="text-xs font-semibold text-gray-500">{t.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>


    </div>
  );
}
